import {
  useEffect,
  useState,
  forwardRef,
  type ChangeEvent,
  type ComponentProps,
} from "react";
import { useForm, type Control, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { useToast } from "@/shared/hooks/use-toast";

import {
  useReceivableShareConfig,
  useCreateReceivableShareConfig,
  useUpdateReceivableShareConfig,
} from "../hooks";
import { CreateReceivableShareConfigPayload } from "../utils/types";
import {
  shareConfigFormSchema,
  shareConfigDefaultValues,
  sumEqualsHundred,
  type ShareConfigFormValues,
} from "../model/share-config.schema";

type FormData = ShareConfigFormValues;

const decimalToPercent = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }
  return value * 100;
};

const percentToDecimal = (value: number) => value / 100;

export function ReceivableShareConfigForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { shareConfig, isLoading } = useReceivableShareConfig(id, { enabled: isEdit });
  const { createShareConfig, isLoading: creating } = useCreateReceivableShareConfig();
  const { updateShareConfig, isLoading: updating } = useUpdateReceivableShareConfig();

  const form = useForm<FormData>({
    resolver: zodResolver(shareConfigFormSchema),
    defaultValues: shareConfigDefaultValues,
  });

  useEffect(() => {
    if (!shareConfig) return;
    form.reset({
      name: shareConfig.name,
      description: shareConfig.description ?? "",
      trafficPercentage: decimalToPercent(shareConfig.trafficPercentage),
      trafficMobileClicksPercentage: decimalToPercent(shareConfig.trafficMobileClicksPercentage),
      trafficWebClicksPercentage: decimalToPercent(shareConfig.trafficWebClicksPercentage),
      trafficConversionClicksPercentage: decimalToPercent(shareConfig.trafficConversionClicksPercentage),
      averageTrafficPercentage: decimalToPercent(shareConfig.averageTrafficPercentage),
      averageTrafficMobileClicksPercentage: decimalToPercent(shareConfig.averageTrafficMobileClicksPercentage),
      averageTrafficWebClicksPercentage: decimalToPercent(shareConfig.averageTrafficWebClicksPercentage),
      averageTrafficConversionClicksPercentage: decimalToPercent(shareConfig.averageTrafficConversionClicksPercentage),
      monthQuantityForAverage: shareConfig.monthQuantityForAverage,
      presetUsePercentage: decimalToPercent(shareConfig.presetUsePercentage),
    });
  }, [shareConfig, form]);

  const mapFormToPayload = (data: FormData): CreateReceivableShareConfigPayload => ({
    name: data.name,
    description: data.description || undefined,
    trafficPercentage: percentToDecimal(data.trafficPercentage),
    trafficMobileClicksPercentage: percentToDecimal(data.trafficMobileClicksPercentage),
    trafficWebClicksPercentage: percentToDecimal(data.trafficWebClicksPercentage),
    trafficConversionClicksPercentage: percentToDecimal(data.trafficConversionClicksPercentage),
    averageTrafficPercentage: percentToDecimal(data.averageTrafficPercentage),
    averageTrafficMobileClicksPercentage: percentToDecimal(data.averageTrafficMobileClicksPercentage),
    averageTrafficWebClicksPercentage: percentToDecimal(data.averageTrafficWebClicksPercentage),
    averageTrafficConversionClicksPercentage: percentToDecimal(data.averageTrafficConversionClicksPercentage),
    monthQuantityForAverage: data.monthQuantityForAverage,
    presetUsePercentage: percentToDecimal(data.presetUsePercentage),
  });

  const isSubmitting = creating || updating;
  const [
    presetUseValue,
    trafficPercentageValue,
    averageTrafficPercentageValue,
    trafficMobileValue,
    trafficWebValue,
    trafficConversionValue,
    averageTrafficMobileValue,
    averageTrafficWebValue,
    averageTrafficConversionValue,
  ] = form.watch([
    "presetUsePercentage",
    "trafficPercentage",
    "averageTrafficPercentage",
    "trafficMobileClicksPercentage",
    "trafficWebClicksPercentage",
    "trafficConversionClicksPercentage",
    "averageTrafficMobileClicksPercentage",
    "averageTrafficWebClicksPercentage",
    "averageTrafficConversionClicksPercentage",
  ]);

  // Validação dos três campos principais
  const mainPercentagesValues = [
    presetUseValue ?? 0,
    trafficPercentageValue ?? 0,
    averageTrafficPercentageValue ?? 0,
  ];
  const mainPercentagesSum = mainPercentagesValues.reduce((acc, value) => acc + value, 0);
  const isMainPercentagesValid = sumEqualsHundred(mainPercentagesValues);

  const trafficDistributionValues = [
    trafficMobileValue ?? 0,
    trafficWebValue ?? 0,
    trafficConversionValue ?? 0,
  ];
  const averageDistributionValues = [
    averageTrafficMobileValue ?? 0,
    averageTrafficWebValue ?? 0,
    averageTrafficConversionValue ?? 0,
  ];

  const trafficDistributionSum = trafficDistributionValues.reduce((acc, value) => acc + value, 0);
  const averageDistributionSum = averageDistributionValues.reduce((acc, value) => acc + value, 0);

  const isTrafficDistributionValid = sumEqualsHundred(trafficDistributionValues);
  const isAverageDistributionValid = sumEqualsHundred(averageDistributionValues);

  const onSubmit = async (data: FormData) => {
    const payload = mapFormToPayload(data);
    console.log(payload);

    try {
      if (isEdit && id) {
        await updateShareConfig({ id, data: payload });
        toast({ title: "Configuração atualizada com sucesso", variant: "success" });
      } else {
        await createShareConfig(payload);
        toast({ title: "Configuração criada com sucesso", variant: "success" });
      }
      navigate("/receivable-share-configs");
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Carregando...</h1>
            <p className="text-muted-foreground">Buscando dados da configuração</p>
          </div>
        </div>
        <Card>
          <CardContent className="flex h-48 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/receivable-share-configs")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? "Editar Configuração de Rateio" : "Nova Configuração de Rateio"}
          </h1>
          <p className="text-muted-foreground">
            Defina os pesos utilizados no cálculo de distribuição dos recebíveis.
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Seção 1: Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>Identifique e descreva a configuração de rateio.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Rateio Apple Q4/2025" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Ex: Apple payouts Q4/2025 - usa preset_use + tráfego_médio"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Seção 2: Percentuais Principais */}
          <Card>
            <CardHeader>
              <CardTitle>Percentuais Principais</CardTitle>
              <CardDescription>
                Defina a distribuição entre os três componentes principais. A soma deve totalizar 100%.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* preset_use_percentage - campo único */}
              <div>
                <FormField
                  control={form.control}
                  name="presetUsePercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>preset_use_percentage</FormLabel>
                      <FormControl>
                        <PercentInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* traffic_percentage com seus subvalores */}
              <div className="space-y-4 rounded-lg border-2 border-gray-500/20 bg-gray-500/5 p-4">
                <div className="flex items-center gap-2">
                  <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                  <p className="text-sm font-semibold text-foreground">traffic_percentage</p>
                </div>
                <FormField
                  control={form.control}
                  name="trafficPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Valor principal</FormLabel>
                      <FormControl>
                        <PercentInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="space-y-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Subdivisão do traffic_percentage {trafficPercentageValue && trafficPercentageValue > 0 ? "(somam 100%)" : "(desabilitado - traffic_percentage = 0%)"}
                  </p>
                  <div className="space-y-3">
                    <PercentSubField
                      control={form.control}
                      name="trafficMobileClicksPercentage"
                      label="traffic_mobile_clicks_percentage"
                      disabled={isSubmitting || !trafficPercentageValue || trafficPercentageValue === 0}
                    />
                    <PercentSubField
                      control={form.control}
                      name="trafficWebClicksPercentage"
                      label="traffic_web_clicks_percentage"
                      disabled={isSubmitting || !trafficPercentageValue || trafficPercentageValue === 0}
                    />
                    <PercentSubField
                      control={form.control}
                      name="trafficConversionClicksPercentage"
                      label="traffic_conversion_clicks_percentage"
                      disabled={isSubmitting || !trafficPercentageValue || trafficPercentageValue === 0}
                    />
                  </div>
                  {trafficPercentageValue && trafficPercentageValue > 0 ? (
                    <>
                      <p
                        className={`text-xs font-medium ${isTrafficDistributionValid ? "text-emerald-500" : "text-destructive"
                          }`}
                      >
                        Soma dos subvalores: {formatPercentWithSymbol(trafficDistributionSum)} / 100%
                      </p>
                      {!isTrafficDistributionValid && (
                        <p className="text-xs text-destructive">Ajuste os valores até completar 100%.</p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Defina um valor maior que 0% em traffic_percentage para habilitar os subvalores.
                    </p>
                  )}
                </div>
              </div>

              {/* average_traffic_percentage com seus subvalores */}
              <div className="space-y-4 rounded-lg border-2 border-gray-500/20 bg-gray-500/5 p-4">
                <div className="flex items-center gap-2">
                  <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                  <p className="text-sm font-semibold text-foreground">average_traffic_percentage</p>
                </div>
                <FormField
                  control={form.control}
                  name="averageTrafficPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Valor principal</FormLabel>
                      <FormControl>
                        <PercentInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="space-y-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Subdivisão do average_traffic_percentage {averageTrafficPercentageValue && averageTrafficPercentageValue > 0 ? "(somam 100%)" : "(desabilitado - average_traffic_percentage = 0%)"}
                  </p>
                  <div className="space-y-3">
                    <PercentSubField
                      control={form.control}
                      name="averageTrafficMobileClicksPercentage"
                      label="average_traffic_mobile_clicks_percentage"
                      disabled={isSubmitting || !averageTrafficPercentageValue || averageTrafficPercentageValue === 0}
                    />
                    <PercentSubField
                      control={form.control}
                      name="averageTrafficWebClicksPercentage"
                      label="average_traffic_web_clicks_percentage"
                      disabled={isSubmitting || !averageTrafficPercentageValue || averageTrafficPercentageValue === 0}
                    />
                    <PercentSubField
                      control={form.control}
                      name="averageTrafficConversionClicksPercentage"
                      label="average_traffic_conversion_clicks_percentage"
                      disabled={isSubmitting || !averageTrafficPercentageValue || averageTrafficPercentageValue === 0}
                    />
                  </div>
                  {averageTrafficPercentageValue && averageTrafficPercentageValue > 0 ? (
                    <>
                      <p
                        className={`text-xs font-medium ${isAverageDistributionValid ? "text-emerald-500" : "text-destructive"
                          }`}
                      >
                        Soma dos subvalores: {formatPercentWithSymbol(averageDistributionSum)} / 100%
                      </p>
                      {!isAverageDistributionValid && (
                        <p className="text-xs text-destructive">Distribua os percentuais até fechar 100%.</p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Defina um valor maior que 0% em average_traffic_percentage para habilitar os subvalores.
                    </p>
                  )}
                </div>
              </div>

              {/* Validação visual dos três campos principais */}
              <div className={`space-y-2 rounded-lg border-2 px-4 py-3 ${
                isMainPercentagesValid 
                  ? 'bg-emerald-500/10 border-emerald-500/50' 
                  : 'bg-destructive/10 border-destructive/50'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">
                    Soma dos percentuais principais
                  </p>
                  <p
                    className={`text-sm font-bold ${isMainPercentagesValid ? "text-emerald-500" : "text-destructive"
                      }`}
                  >
                    {formatPercentWithSymbol(mainPercentagesSum)} / 100%
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border/50">
                  <div className="text-xs">
                    <span className="text-muted-foreground block mb-1">preset_use_percentage</span>
                    <span className="font-semibold text-foreground">{formatPercentWithSymbol(presetUseValue ?? 0)}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground block mb-1">traffic_percentage</span>
                    <span className="font-semibold text-foreground">{formatPercentWithSymbol(trafficPercentageValue ?? 0)}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground block mb-1">average_traffic_percentage</span>
                    <span className="font-semibold text-foreground">{formatPercentWithSymbol(averageTrafficPercentageValue ?? 0)}</span>
                  </div>
                </div>
                {!isMainPercentagesValid && (
                  <p className="text-xs text-destructive mt-2">
                    ⚠️ Ajuste os valores até que a soma seja exatamente 100%.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Seção 4: Configurações Adicionais */}
          <Card>
            <CardHeader>
              <CardTitle>Configurações Adicionais</CardTitle>
              <CardDescription>Parâmetros complementares para o cálculo de média histórica.</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="monthQuantityForAverage"
                render={({ field }) => (
                  <FormItem className="max-w-xs">
                    <FormLabel>month_quantity_for_average</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="1" 
                        step="1" 
                        placeholder="Ex: 3"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription>
                      Número de meses utilizados para calcular a média histórica de tráfego.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Seção 5: Ações */}
          <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate("/receivable-share-configs")} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    isEdit ? "Salvar alterações" : "Criar configuração"
                  )}
                </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

type PercentInputProps = Omit<ComponentProps<typeof Input>, "value" | "onChange" | "type"> & {
  value?: number;
  onChange?: (value: number | undefined) => void;
};

const PercentInput = forwardRef<HTMLInputElement, PercentInputProps>(
  ({ value, onChange, onBlur, onFocus, placeholder = "0,00", ...rest }, ref) => {
    const [digits, setDigits] = useState("");

    useEffect(() => {
      if (value === undefined || Number.isNaN(value)) {
        setDigits("");
        return;
      }
      const asDigits = Math.round(Math.min(100, Math.max(0, value)) * 100);
      setDigits(String(asDigits));
    }, [value]);

    const displayValue = digits
      ? formatPercent(Math.min(100, Number(digits) / 100))
      : "";

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const onlyDigits = event.target.value.replace(/\D/g, "");
      if (!onlyDigits) {
        setDigits("");
        onChange?.(undefined);
        return;
      }

      const cappedDigits = onlyDigits.slice(0, 5);
      let numeric = Number(cappedDigits) / 100;

      if (Number.isNaN(numeric)) {
        setDigits("");
        onChange?.(undefined);
        return;
      }

      numeric = Math.min(100, numeric);
      const normalizedDigits = String(Math.round(numeric * 100));
      setDigits(normalizedDigits);
      onChange?.(numeric);
    };

    return (
      <Input
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        inputMode="numeric"
        placeholder={placeholder}
        {...rest}
      />
    );
  },
);

PercentInput.displayName = "PercentInput";

const formatPercent = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatPercentWithSymbol = (value: number) =>
  `${formatPercent(Number.isFinite(value) ? value : 0)}%`;

type PercentSubFieldProps<TFieldName extends FieldPath<FormData>> = {
  control: Control<FormData>;
  name: TFieldName;
  label: string;
  disabled: boolean;
};

function PercentSubField<TFieldName extends FieldPath<FormData>>({
  control,
  name,
  label,
  disabled,
}: PercentSubFieldProps<TFieldName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="font-mono text-xs uppercase tracking-wide text-muted-foreground break-words">
            {label}
          </FormLabel>
          <FormControl>
            <PercentInput
              ref={field.ref}
              name={field.name}
              value={Number(field.value)}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
